import { useId, useLayoutEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'

/**
 * The butler's thinking, shown as it streams (WP 9.12) — the summary the
 * API sends with `display: 'summarized'`, never the raw chain.
 *
 * **Before the answer starts**: one quiet block under Doug's bubble — a
 * small "Thinking" label, then the summary, clamped to three lines with the
 * newest at the foot (the older lines slide up under a fade rather than the
 * block growing down the screen).
 *
 * **Once the first word of the answer arrives** (`answering`): it folds to
 * one line, "Thought for a moment", which opens on a tap to the whole
 * summary. It is never persisted — a reload shows nothing for it, which is
 * the point: it is how the turn felt while it ran, not part of what was
 * said.
 *
 * Reduced motion: the label does not pulse, and nothing animates the fold.
 */
export function Thinking({ text, answering }: { text: string; answering: boolean }) {
  const [open, setOpen] = useState(false)
  const bodyId = useId()
  const clip = useRef<HTMLDivElement>(null)
  const inner = useRef<HTMLParagraphElement>(null)
  const [overflows, setOverflows] = useState(false)

  // The fade only when there is something above to fade: a one-line summary
  // with its first words faded out would read as a rendering fault.
  useLayoutEffect(() => {
    if (answering) return
    const c = clip.current
    const p = inner.current
    if (c && p) setOverflows(p.offsetHeight > c.clientHeight + 1)
  }, [text, answering])

  const summary = text.trim()
  if (!summary) return null

  if (!answering) {
    return (
      <div className="chat-think" data-live="">
        <p className="chat-think__label">Thinking</p>
        <div ref={clip} className="chat-think__clip" data-overflows={overflows || undefined}>
          <p ref={inner} className="chat-think__text">{summary}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="chat-think">
      <button
        type="button"
        className="chat-fold"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen(o => !o)}
      >
        <span className="chat-fold__text">Thought for a moment</span>
        <ChevronDown className="chat-fold__chev" aria-hidden="true" />
      </button>
      {open && <p id={bodyId} className="chat-think__text chat-think__text--full">{summary}</p>}
    </div>
  )
}
